import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Truck, ArrowLeft, MapPin, Package, Calendar, User, Mail, Phone, FileText, CreditCard, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingPage() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    pickup_address: "",
    delivery_address: "",
    preferred_date: "",
    notes: "",
  });

  useEffect(() => {
    fetchQuote();
    // Apply rebook prefill if present
    try {
      const key = `rebook_prefill_${quoteId}`;
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const prefill = JSON.parse(raw);
        setFormData(prev => ({ ...prev, ...prefill }));
        sessionStorage.removeItem(key);
        toast.success("Previous booking details loaded");
      }
    } catch {}
  }, [quoteId]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API}/quote/${quoteId}`);
      setQuote(response.data);
    } catch (error) {
      toast.error("Quote not found or expired");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone || 
        !formData.pickup_address || !formData.delivery_address) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      // Create booking
      const bookingResponse = await axios.post(`${API}/booking`, {
        quote_id: quoteId,
        ...formData,
      });
      
      const bookingId = bookingResponse.data.id;
      
      // Create checkout session
      const checkoutResponse = await axios.post(`${API}/checkout`, {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      
      // Redirect to Stripe checkout
      window.location.href = checkoutResponse.data.url;
      
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create booking";
      toast.error(message);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading quote...</p>
        </div>
      </div>
    );
  }

  if (!quote) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                Transport Anything 247
              </span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Quote Calculator</span>
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <Card className="calculator-card">
              <CardHeader>
                <CardTitle className="calculator-header text-2xl">Complete Your Booking</CardTitle>
                <CardDescription>Fill in your details to proceed with payment</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitBooking} className="space-y-6">
                  {/* Contact Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <User className="h-5 w-5 text-orange-500" />
                      Contact Information
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="form-label">Full Name *</Label>
                        <Input
                          id="name"
                          data-testid="customer-name-input"
                          placeholder="John Smith"
                          value={formData.customer_name}
                          onChange={(e) => handleInputChange("customer_name", e.target.value)}
                          className="h-12"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="form-label">Phone Number *</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            id="phone"
                            data-testid="customer-phone-input"
                            placeholder="07123 456789"
                            value={formData.customer_phone}
                            onChange={(e) => handleInputChange("customer_phone", e.target.value)}
                            className="pl-10 h-12"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <Label htmlFor="email" className="form-label">Email Address *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="email"
                          data-testid="customer-email-input"
                          type="email"
                          placeholder="john@example.com"
                          value={formData.customer_email}
                          onChange={(e) => handleInputChange("customer_email", e.target.value)}
                          className="pl-10 h-12"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Quote confirmation will be sent to this email</p>
                    </div>
                  </div>

                  {/* Addresses */}
                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-orange-500" />
                      Collection & Delivery Addresses
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="pickup" className="form-label">Pickup Address *</Label>
                        <Textarea
                          id="pickup"
                          data-testid="pickup-address-input"
                          placeholder="Enter full pickup address including postcode"
                          value={formData.pickup_address}
                          onChange={(e) => handleInputChange("pickup_address", e.target.value)}
                          className="min-h-[80px]"
                        />
                        <p className="text-xs text-slate-500 mt-1">Postcode: {quote.pickup_postcode}</p>
                      </div>
                      <div>
                        <Label htmlFor="delivery" className="form-label">Delivery Address *</Label>
                        <Textarea
                          id="delivery"
                          data-testid="delivery-address-input"
                          placeholder="Enter full delivery address including postcode"
                          value={formData.delivery_address}
                          onChange={(e) => handleInputChange("delivery_address", e.target.value)}
                          className="min-h-[80px]"
                        />
                        <p className="text-xs text-slate-500 mt-1">Postcode: {quote.delivery_postcode}</p>
                      </div>
                    </div>
                  </div>

                  {/* Preferred Date & Notes */}
                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-orange-500" />
                      Schedule & Notes
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="date" className="form-label">Preferred Collection Date</Label>
                        <Input
                          id="date"
                          data-testid="preferred-date-input"
                          type="date"
                          value={formData.preferred_date}
                          onChange={(e) => handleInputChange("preferred_date", e.target.value)}
                          className="h-12"
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div>
                        <Label htmlFor="notes" className="form-label">Additional Notes</Label>
                        <Input
                          id="notes"
                          data-testid="notes-input"
                          placeholder="e.g. Call before arrival"
                          value={formData.notes}
                          onChange={(e) => handleInputChange("notes", e.target.value)}
                          className="h-12"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    data-testid="proceed-payment-btn"
                    disabled={submitting}
                    className="w-full h-14 bg-orange-500 hover:bg-orange-600 text-white font-medium text-lg"
                  >
                    {submitting ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Processing...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-5 w-5" />
                        <span>Proceed to Payment - £{quote.total_cost.toFixed(2)}</span>
                      </div>
                    )}
                  </Button>
                  
                  <p className="text-center text-xs text-slate-500">
                    Secure payment powered by Stripe. You will be redirected to complete payment.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Quote Summary */}
          <div className="lg:col-span-1">
            <Card className="calculator-card sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5 text-orange-500" />
                  Quote Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Route Info */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-orange-500" />
                      <div className="w-0.5 h-8 bg-slate-300" />
                      <div className="w-3 h-3 rounded-full bg-slate-900" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-slate-500">Pickup</p>
                      <p className="font-medium text-slate-900">{quote.pickup_postcode}</p>
                      <div className="my-2" />
                      <p className="text-sm text-slate-500">Delivery</p>
                      <p className="font-medium text-slate-900">{quote.delivery_postcode}</p>
                    </div>
                  </div>
                </div>

                {/* Item Details */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Item Type</span>
                    <span className="font-medium text-slate-900 capitalize">{quote.item_type}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Size</span>
                    <span className="font-medium text-slate-900 capitalize">{quote.size}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Distance</span>
                    <span className="font-medium text-slate-900">{quote.distance_miles} miles</span>
                  </div>
                </div>

                {/* Price Breakdown */}
                <div className="border-t border-slate-200 pt-4 space-y-2">
                  {Object.entries(quote.breakdown).map(([label, value]) => (
                    value > 0 && (
                      <div key={label} className="flex justify-between text-sm">
                        <span className="text-slate-500 text-xs">{label.split('(')[0]}</span>
                        <span className="font-mono text-slate-900">£{value.toFixed(2)}</span>
                      </div>
                    )
                  ))}
                </div>

                {/* Total */}
                <div className="bg-slate-900 text-white rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Total</span>
                    <span data-testid="booking-total" className="text-2xl font-bold font-mono text-orange-500">
                      £{quote.total_cost.toFixed(2)}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-slate-500 text-center">
                  Quote valid for 24 hours. Prices include VAT.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
